import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateSession, useGetUser, usePaySession } from '@/services';
import { setUserData } from '@/utils/cookies';
import {
	clearMatchReturnPending,
	isMatchReturnPending,
	MATCH_PAGE_PATH,
} from '@/utils/matchHandoff';
import {
	clearSchedulingPayDraftSilently,
	clearUserScopedQueryCache,
	encodeUrl,
	executeSchedulingPayAfterAuth,
	isSchedulingPayAfterAuthPending,
	readSchedulingPayDraft,
	schedulingSelectedDateStillBookable,
} from '@/utils';

const OauthCallback = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const isPublicSchedulingLogin = localStorage.getItem('pAuth');
	const isPublicSchedulingRegister = localStorage.getItem('rAuth');

	const { data, isLoading } = useGetUser({
		isMe: true,
	});

	const { mutateAsync: createSession } = useCreateSession();
	const { mutateAsync: paySession } = usePaySession();

	useEffect(() => {
		if (!isLoading && data) {
			void clearUserScopedQueryCache(queryClient).then(async () => {
				setUserData(data);

				// OAuth iniciado no /match: conta completa volta direto; incompleta termina o cadastro
				// (a flag é consumida no complemento, que também volta para /match).
				if (isMatchReturnPending()) {
					if (data.is_complete) {
						clearMatchReturnPending();
						navigate(MATCH_PAGE_PATH, { replace: true });
						return;
					}
					navigate('/cadastro/info');
					return;
				}

				const encodedParam = encodeUrl({
					s: 3,
				});

				if (
					isPublicSchedulingLogin &&
					isSchedulingPayAfterAuthPending()
				) {
					const schedulingBody = readSchedulingPayDraft();
					if (
						!schedulingBody?.selected_date ||
						!schedulingSelectedDateStillBookable(
							schedulingBody.selected_date,
						)
					) {
						clearSchedulingPayDraftSilently();
						localStorage.removeItem('pAuth');
						navigate('/agendamento', { replace: true });
						return;
					}

					localStorage.removeItem('pAuth');
					const result = await executeSchedulingPayAfterAuth({
						createSession,
						paySession,
					});
					if (result.ok && result.redirected) {
						return;
					}
					navigate(`/agendamento?d=${encodeUrl({ s: 4 })}&s_s=true`, {
						replace: true,
					});
					return;
				}

				if (isPublicSchedulingRegister) {
					const storageSessionData =
						localStorage.getItem('session_data');
					if (storageSessionData) {
						const sessionData = JSON.parse(storageSessionData);
						localStorage.setItem('is_public_register', 'true');
						localStorage.setItem(
							'session_data',
							JSON.stringify({
								...sessionData,
								user_id: data.id,
							}),
						);

						navigate(`/agendamento?d=${encodedParam}`);
						return;
					}
				}

				if (!data.is_complete) {
					navigate('/cadastro/info');
					return;
				}

				clearSchedulingPayDraftSilently();
				navigate('/formulario');
			});
			return;
		}

		if (!isLoading && !data) {
			navigate('/login');
		}
	}, [
		createSession,
		data,
		isLoading,
		isPublicSchedulingLogin,
		isPublicSchedulingRegister,
		navigate,
		paySession,
		queryClient,
		searchParams,
	]);

	return <></>;
};

export { OauthCallback };
